import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import enGB from './locales/en-GB.json';

export const LANGUAGES = [
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'fr', label: 'Français' },
] as const;

/**
 * §17 perf: only English (the fallback) ships in the first download; other languages are their own chunk, fetched
 * when someone uses them — so new features' strings don't grow everyone's first load twice over.
 */
const LAZY: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  fr: () => import('./locales/fr.json'),
};
const baseOf = (lng: string) => Object.keys(LAZY).find((l) => lng === l || lng.startsWith(`${l}-`)) ?? null;

let extra: Promise<void> | null = null;
/**
 * §23 M12.6 English is split: the first download carries only the strings the shell uses (en-GB.json); the rest
 * (en-GB.extra.json) arrives with the first lazy page, or when the browser is idle. scripts/locale-check.mjs keeps
 * the split honest. Other languages are one file each, fetched whole.
 */
export function loadExtra(): Promise<void> {
  return (extra ??= import('./locales/en-GB.extra.json').then(
    (m) => {
      for (const lng of ['en-GB', 'en'])
        i18n.addResourceBundle(lng, 'translation', m.default as Record<string, unknown>, true, false);
    },
    () => {
      extra = null; // offline for a moment: try again next time
    },
  ));
}

async function ensure(lng: string) {
  const base = baseOf(lng);
  if (base && !i18n.hasResourceBundle(base, 'translation'))
    i18n.addResourceBundle(base, 'translation', (await LAZY[base]!()).default);
}

/** Resolves once the person's language is loaded (main.tsx waits for it before the first render). */
export const i18nReady = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Any English variant falls back to en-GB; other unsupported languages fall back to en-GB too.
    resources: { 'en-GB': { translation: enGB }, en: { translation: enGB } },
    partialBundledLanguages: true,
    fallbackLng: 'en-GB',
    supportedLngs: ['en-GB', 'en', 'fr'],
    interpolation: { escapeValue: false },
    // re-render when the extra English strings arrive
    react: { bindI18nStore: 'added' },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'fyr.lang',
    },
  })
  // The REQUESTED language (resolvedLanguage is still the fallback until its strings have arrived).
  .then(() => ensure(i18n.language))
  .then(() => i18n.changeLanguage(i18n.language))
  .then(
    () => undefined,
    () => undefined,
  );

/** Switch language, fetching it first if it isn't loaded yet. */
export async function setLanguage(code: string) {
  await ensure(code);
  await i18n.changeLanguage(code);
}

i18n.on('languageChanged', (lng) => document.documentElement.setAttribute('lang', lng));

export default i18n;
